import React, { useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet";
import { ArrowLeft, Calendar, Clock, User } from "lucide-react";
import { BlogData } from "../../../utils/blogData";

const BlogId = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const blog = BlogData[id];

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [id]);

  // Blog metadata based on ID
  const getBlogMetadata = (blogId) => {
    const metadata = {
      16052024: {
        title: "The Secret to Hiring High-Performing Employees",
        description:
          "Why qualifications alone aren't enough — and how behavioral science can transform your hiring process.",
        datePublished: "2025-01-08",
        dateModified: "2025-01-08",
        keywords:
          "hiring high-performing employees, talent assessment, behavioral science, employee performance, recruitment strategy",
        category: "Talent Acquisition",
        readTime: "5 min read",
        author: "SelfSync Team",
      },
      15052024: {
        title:
          "You're Probably Hiring Wrong — Here's the Fix That's Backed by Science",
        description:
          "Skills matter, but they don't guarantee success. Discover the science-backed solution to better hiring.",
        datePublished: "2025-02-11",
        dateModified: "2025-02-11",
        keywords:
          "hiring mistakes, science-backed hiring, recruitment science, talent acquisition, hiring strategy",
        category: "Hiring Strategies",
        readTime: "4 min read",
        author: "SelfSync Team",
      },
      12052024: {
        title: "One Simple Test That Can Save You From a Bad Hire",
        description:
          "A bad hire isn't just disappointing — it's expensive. Learn how to avoid costly hiring mistakes.",
        datePublished: "2025-03-12",
        dateModified: "2025-03-12",
        keywords:
          "bad hire prevention, hiring test, cost of bad hire, recruitment assessment, hiring decisions",
        category: "Workforce Optimization",
        readTime: "6 min read",
        author: "SelfSync Team",
      },
    };
    return metadata[blogId] || {};
  };

  const blogMetadata = getBlogMetadata(id);

  // Schema markup for individual blog post
  const blogPostSchema = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: blogMetadata.title,
    description: blogMetadata.description,
    url: `https://selfsync.ai/blogs/${id}`,
    datePublished: blogMetadata.datePublished,
    dateModified: blogMetadata.dateModified,
    author: {
      "@type": "Organization",
      name: "SelfSync",
      url: "https://selfsync.ai",
    },
    publisher: {
      "@type": "Organization",
      name: "SelfSync",
      logo: {
        "@type": "ImageObject",
        url: "https://selfsync.ai/static/media/SelfSync%20Logo.df7570a5489657df44eb.png",
      },
    },
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": `https://selfsync.ai/blogs/${id}`,
    },
    image: {
      "@type": "ImageObject",
      url: "https://selfsync.ai/static/media/SelfSync%20Logo.df7570a5489657df44eb.png",
    },
    articleSection: blogMetadata.category,
    wordCount: "800",
  };

  if (!blog) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center max-w-md mx-auto px-6">
          <div className="text-6xl mb-6">📄</div>
          <h1 className="text-2xl font-bold text-gray-800 mb-4">
            Blog Post Not Found
          </h1>
          <p className="text-gray-600 mb-8">
            The blog post you're looking for doesn't exist or may have been
            moved.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => navigate("/blogs")}
              className="bg-purple-600 text-white px-6 py-3 rounded-lg hover:bg-purple-700 transition-colors"
            >
              Back to Blog
            </button>
            <button
              onClick={() => navigate("/")}
              className="border border-purple-600 text-purple-600 px-6 py-3 rounded-lg hover:bg-purple-600 hover:text-white transition-colors"
            >
              Go Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{blogMetadata.title} | SelfSync Blog</title>
        <meta name="description" content={blogMetadata.description} />
        <meta name="keywords" content={blogMetadata.keywords} />
        <link rel="canonical" href={`https://selfsync.ai/blogs/${id}`} />

        {/* Open Graph tags */}
        <meta
          property="og:title"
          content={`${blogMetadata.title} | SelfSync Blog`}
        />
        <meta property="og:description" content={blogMetadata.description} />
        <meta property="og:url" content={`https://selfsync.ai/blogs/${id}`} />
        <meta property="og:type" content="article" />
        <meta
          property="og:image"
          content="https://selfsync.ai/static/media/SelfSync%20Logo.df7570a5489657df44eb.png"
        />
        <meta
          property="article:published_time"
          content={blogMetadata.datePublished}
        />
        <meta
          property="article:modified_time"
          content={blogMetadata.dateModified}
        />
        <meta property="article:author" content="SelfSync" />
        <meta property="article:section" content={blogMetadata.category} />

        {/* Twitter Card tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content={`${blogMetadata.title} | SelfSync Blog`}
        />
        <meta name="twitter:description" content={blogMetadata.description} />
        <meta
          name="twitter:image"
          content="https://selfsync.ai/static/media/SelfSync%20Logo.df7570a5489657df44eb.png"
        />

        {/* Schema markup */}
        <script type="application/ld+json">
          {JSON.stringify(blogPostSchema)}
        </script>
      </Helmet>

      {/* Navigation Breadcrumb */}
      <div className="bg-gray-50 py-4 mt-20">
        <div className="max-w-6xl mx-auto px-6">
          <button
            onClick={() => navigate("/blogs")}
            className="flex items-center text-purple-600 hover:text-purple-800 transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Blog
          </button>
        </div>
      </div>

      {/* Article Header */}
      <div className="bg-white pt-5">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6 text-sm text-gray-600 mx-4">
              <span className="bg-purple-100 text-purple-700 px-3 py-1.5 rounded-full font-medium flex items-center justify-center">
                {blogMetadata.category}
              </span>
              <div className="flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                {new Date(blogMetadata.datePublished).toLocaleDateString(
                  "en-US",
                  {
                    month: "long",
                    day: "numeric",
                    year: "numeric",
                  }
                )}
              </div>
              <div className="flex items-center gap-1">
                <Clock className="w-4 h-4" />
                {blogMetadata.readTime}
              </div>
              <div className="flex items-center gap-1">
                <User className="w-4 h-4" />
                {blogMetadata.author}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Blog Content */}
      <div dangerouslySetInnerHTML={{ __html: blog.content }} />

      {/* Related Articles CTA */}
      {/* <div className="bg-gray-50 py-16">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h3 className="text-2xl font-bold text-gray-900 mb-4">
            Explore More Insights
          </h3>
          <p className="text-gray-600 mb-8">
            Discover more expert insights on AI-powered hiring and talent assessment
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => navigate("/blogs")}
              className="bg-purple-600 text-white px-8 py-3 rounded-lg hover:bg-purple-700 transition-colors"
            >
              View All Articles
            </button>
            <button
              onClick={() => navigate("/schedule-demo")}
              className="border border-purple-600 text-purple-600 px-8 py-3 rounded-lg hover:bg-purple-600 hover:text-white transition-colors"
            >
              Schedule Demo
            </button>
          </div>
        </div>
      </div> */}
    </>
  );
};

export default BlogId;
